





































  import { Component, Mixins, Prop } from 'vue-property-decorator';
  import { VueCSSClasses } from '../utils/types';
  import { Debounce } from './decorators/debounce.decorators';
  import { dynamicPropsMixin } from './dynamic-props.mixin';

  /**
   * This component allows for any other component or element inside it to be horizontally
   * navigable. It also implements customizable buttons as well as other minor customizations to its
   * general behavior.
   *
   * Additionally, this component exposes the following props to modify the classes of the
   * elements: `buttonClass`.
   *
   * @public
   */
  @Component
  export default class SlidingPanel extends Mixins(
    dynamicPropsMixin(['buttonClass', 'scrollContainerClass'])
  ) {
    /**
     * Scroll factor that will dictate how much the scroll moves when pressing a navigation button.
     *
     * @public
     */
    @Prop({ default: 0.7 })
    public scrollFactor!: number;

    /**
     * Would make the navigation buttons visible when they're needed or always hide them.
     *
     * @public
     */
    @Prop({ default: true })
    public showButtons!: boolean;

    /**
     * When true, whenever the DOM content in the sliding panel slot changes, it will reset
     * the scroll position to 0.
     *
     * @public
     */
    @Prop({ default: true })
    public resetOnContentChange!: boolean;

    /**
     * Indicates if the scroll is at the start of the sliding panel.
     *
     * @internal
     */
    protected isScrollAtStart = true;

    /**
     * Indicates if the scroll is at the end of the sliding panel.
     *
     * @internal
     */
    protected isScrollAtEnd = true;

    /**
     * HTMLElement referencing the scroll of the component.
     *
     * @internal
     */
    public $refs!: {
      scrollContainer: HTMLElement;
    };

    /**
     * CSS classes to apply based on the scroll position.
     *
     * @returns The CSS classes to apply.
     *
     * @internal
     */
    protected get cssClasses(): VueCSSClasses {
      return {
        'x-sliding-panel-at-start': this.isScrollAtStart,
        'x-sliding-panel-at-end': this.isScrollAtEnd
      };
    }

    /**
     * Initialises browser platform code:
     * - Creates a mutation observer to detect content changes and reset scroll position.
     * - Stores initial size and scroll position values.
     *
     * @internal
     */
    mounted(): void {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const resizeObserver = new ResizeObserver(this.debouncedUpdateScrollPosition);
      resizeObserver.observe(this.$el);
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const contentChangedObserver = new MutationObserver(this.restoreAndUpdateScroll);
      this.$watch(
        () => this.resetOnContentChange,
        shouldReset => {
          if (shouldReset) {
            contentChangedObserver.observe(this.$refs.scrollContainer, {
              attributes: false,
              childList: true,
              subtree: true,
              characterData: false
            });
          } else {
            contentChangedObserver.disconnect();
          }
        },
        { immediate: true }
      );
      this.$on('hook:beforeDestroy', () => {
        contentChangedObserver.disconnect();
        resizeObserver.disconnect();
      });

      this.updateScrollPosition();
    }

    /**
     * Resets the scroll and updates the values of the scroll for the buttons to react.
     *
     * @internal
     */
    @Debounce(50, { leading: true })
    restoreAndUpdateScroll(): void {
      this.$refs.scrollContainer.scroll({ left: 0, behavior: 'smooth' });
      this.updateScrollPosition();
    }

    /**
     * Updates the values of the scroll positions to show or hide the buttons depending on it.
     *
     * @internal
     */
    protected updateScrollPosition(): void {
      if (this.$refs.scrollContainer !== undefined) {
        const { scrollLeft, clientWidth, scrollWidth } = this.$refs.scrollContainer;
        this.isScrollAtStart = !scrollLeft;
        /* The 2 px extra is to fix some cases in some resolutions where the scroll + client size is
         *  less than the scroll width even when the scroll is at the end */
        this.isScrollAtEnd = scrollLeft + clientWidth + 2 >= scrollWidth;
      }
    }

    /**
     * Debounced version of the {@link SlidingPanel.updateScrollPosition}
     * method.
     *
     * @internal
     */
    @Debounce(50, { leading: true })
    debouncedUpdateScrollPosition(): void {
      this.updateScrollPosition();
    }

    /**
     * Scrolls the wrapper element to the left.
     *
     * @internal
     */
    protected scrollLeft(): void {
      this.scrollTo(-this.$refs.scrollContainer.clientWidth);
    }

    /**
     * Scrolls the wrapper element to the right.
     *
     * @internal
     */
    protected scrollRight(): void {
      this.scrollTo(this.$refs.scrollContainer.clientWidth);
    }

    /**
     * Scrolls the wrapper element towards the provided scroll value.
     *
     * @param scrollValue - The value the scroll will go towards.
     *
     * @internal
     */
    protected scrollTo(scrollValue: number): void {
      this.$refs.scrollContainer.scrollBy({
        left: scrollValue * this.scrollFactor,
        behavior: 'smooth'
      });
    }
  }
